import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SpecularButton } from "./ui/specular-button.js";
import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { GlowCard } from "./ui/glow-card.js";
import { Badge } from "./ui/badge.js";
import { ScrollSlider } from "./ui/scroll-slider.js";
import { DropdownSelect } from "./DropdownButton.js";
import { SelectField } from "./ui/select-field.js";
import { CompCompareTable, type CompareSide } from "./CompCompareTable.js";
import { cn } from "../lib/utils.js";
import {
  COMP_STATUS_BADGE, asNum, fmtAmt, fmtDate, humanize, type CompStatus,
} from "../lib/compensation.js";

export interface TimelineRevision {
  id:            string;
  ctcAnnual:     number | string;
  effectiveFrom: string;
  effectiveTo:   string | null;
  reason:        string;
  status:        CompStatus;
}

interface Props {
  employeeId: string;
  /** Newest first, as GET /compensation/snapshots returns it. */
  history: TimelineRevision[];
}

type Side = CompareSide & { reason: string };
type Filter = "year" | "reason" | "status";

/** The approved revision in force before history[i]: what that revision changed from. */
function previousApproved(history: TimelineRevision[], i: number): TimelineRevision | null {
  return history.slice(i + 1).find(h => h.status === "APPROVED") ?? null;
}

function pctChange(from: TimelineRevision | null, to: TimelineRevision): number | null {
  if (!from) return null;
  const b = asNum(from.ctcAnnual);
  return b === 0 ? null : ((asNum(to.ctcAnnual) - b) / b) * 100;
}

/**
 * Revision history as a scrollable strip of cards, filterable by year, reason and status.
 * Pick any two cards to compare them; by default the latest revision is compared with
 * the verified one before it.
 */
export function CompRevisionTimeline({ employeeId, history }: Props) {
  // Years with a revision, newest first. The strip opens on the current year (or the
  // latest year that has one) so a long appraisal history stays short; HR steps back
  // a year at a time with the arrows, or picks "All years".
  const yearsDesc = useMemo(
    () => [...new Set(history.map(h => h.effectiveFrom.slice(0, 4)))].sort().reverse(),
    [history],
  );
  const thisYear = String(new Date().getFullYear());
  const defaultYear = yearsDesc.includes(thisYear) ? thisYear : (yearsDesc[0] ?? "");
  const [year, setYear]     = useState(defaultYear);
  useEffect(() => { if (year && !yearsDesc.includes(year)) setYear(defaultYear); }, [year, yearsDesc, defaultYear]);
  const yearIdx = yearsDesc.indexOf(year);
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState("");
  const [openFilter, setOpenFilter] = useState<Filter | null>(null);

  const defaultPair = useMemo(() => {
    // On load: the latest revision (skipping withdrawn ones) compared with the one in force
    // before it — e.g. the 3rd appraisal against the 2nd, not against the first.
    const i = history.findIndex(h => h.status !== "WITHDRAWN");
    if (i < 0) return history[0] ? [history[0].id] : [];
    const prev = previousApproved(history, i);
    return prev ? [prev.id, history[i]!.id] : [history[i]!.id];
  }, [history]);
  const [selected, setSelected] = useState<string[]>(defaultPair);
  useEffect(() => { setSelected(defaultPair); }, [defaultPair]);

  const years = useMemo(() => yearsDesc.map(y => ({ value: y, label: y })), [yearsDesc]);
  const reasons = useMemo(
    () => [...new Set(history.map(h => h.reason))].map(r => ({ value: r, label: humanize(r) })),
    [history],
  );
  const statuses = useMemo(
    () => [...new Set(history.map(h => h.status))].map(s => ({ value: s, label: COMP_STATUS_BADGE[s]?.label ?? humanize(s) })),
    [history],
  );

  const visible = history
    .map((h, i) => ({ h, prev: previousApproved(history, i) }))
    .filter(({ h }) =>
      (!year || h.effectiveFrom.startsWith(year)) &&
      (!reason || h.reason === reason) &&
      (!status || h.status === status));

  function toggle(id: string) {
    setSelected(sel => sel.includes(id)
      ? sel.filter(x => x !== id)
      : [...sel, id].slice(-2)); // a third pick drops the oldest pick
  }

  // Order the pair by effective date so "before" is always the earlier revision.
  const pair = history
    .filter(h => selected.includes(h.id))
    .sort((a, b) => a.effectiveFrom.localeCompare(b.effectiveFrom) || 0);
  const beforeId = pair.length === 2 ? pair[0]!.id : null;
  const afterId  = pair.length >= 1 ? pair[pair.length - 1]!.id : null;

  const [before, setBefore] = useState<Side | null>(null);
  const [after, setAfter]   = useState<Side | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  useEffect(() => {
    if (!afterId) { setBefore(null); setAfter(null); return; }
    let cancelled = false;
    setLoadErr(null);
    const base = `/employees/${employeeId}/compensation/snapshots`;
    Promise.all([
      beforeId ? api.get<Side>(`${base}/${beforeId}`) : Promise.resolve(null),
      api.get<Side>(`${base}/${afterId}`),
    ])
      .then(([b, a]) => { if (!cancelled) { setBefore(b?.data ?? null); setAfter(a.data); } })
      .catch(e => { logError("CompRevisionTimeline.load", e); if (!cancelled) setLoadErr(errMsg(e) ?? "Couldn't load the revisions."); });
    return () => { cancelled = true; };
  }, [employeeId, beforeId, afterId]);

  const filterProps = (f: Filter) => ({
    open: openFilter === f,
    onToggle: () => setOpenFilter(o => (o === f ? null : f)),
    onClose: () => setOpenFilter(null),
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <SpecularButton
            size="sm" className="w-8 px-0" aria-label="Previous year"
            disabled={yearIdx < 0 || yearIdx >= yearsDesc.length - 1}
            onClick={() => setYear(yearsDesc[yearIdx + 1]!)}
          >
            <ChevronLeft className="h-4 w-4" />
          </SpecularButton>
          <DropdownSelect value={year} onChange={setYear} options={years} placeholder="All years" ariaLabel="Year" {...filterProps("year")} />
          <SpecularButton
            size="sm" className="w-8 px-0" aria-label="Next year"
            disabled={yearIdx <= 0}
            onClick={() => setYear(yearsDesc[yearIdx - 1]!)}
          >
            <ChevronRight className="h-4 w-4" />
          </SpecularButton>
        </div>
        <DropdownSelect value={reason} onChange={setReason} options={reasons}  placeholder="All reasons"  ariaLabel="Reason" {...filterProps("reason")} />
        <DropdownSelect value={status} onChange={setStatus} options={statuses} placeholder="All statuses" ariaLabel="Status" {...filterProps("status")} />
      </div>

      {visible.length === 0 ? (
        <p className="m-0 py-4 text-center text-[13px] text-surface-400">
          No revisions match these filters{year && ` in ${year}`}. Use the arrows or pick “All years” to see the rest.
        </p>
      ) : (
        <ScrollSlider className="py-1">
          {visible.map(({ h, prev }) => {
            const badge = COMP_STATUS_BADGE[h.status];
            const pct = pctChange(prev, h);
            const isSelected = selected.includes(h.id);
            return (
              <GlowCard
                key={h.id}
                as="button"
                type="button"
                size="sm"
                tone="brand"
                glow="low"
                spotlight="low"
                selected={isSelected}
                aria-pressed={isSelected}
                onClick={() => toggle(h.id)}
                width={220}
                className="shrink-0 text-left"
              >
                {/* Compact: three lines — date/period, amount + change, status + reason. */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between gap-2 text-[11px]">
                    <span className="truncate text-surface-500">
                      <span className="font-semibold">{fmtDate(h.effectiveFrom)}</span>
                      {h.status === "APPROVED" && (h.effectiveTo ? ` · until ${fmtDate(h.effectiveTo)}` : " · Current")}
                    </span>
                    {isSelected && <span className="shrink-0 text-[10px] font-bold uppercase text-brand-600">Comparing</span>}
                  </div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[15px] font-bold tabular-nums">₹{fmtAmt(asNum(h.ctcAnnual))}</span>
                    <span className={cn(
                      "text-[11px] font-semibold tabular-nums",
                      pct === null ? "text-surface-400" : pct >= 0 ? "text-[var(--color-success-fg)]" : "text-[var(--color-error-fg)]",
                    )}>
                      {pct === null ? "First" : `${pct >= 0 ? "▲" : "▼"} ${Math.abs(pct).toFixed(1)}%`}
                    </span>
                  </div>
                  <div className="flex min-w-0 items-center gap-1.5">
                    {badge && <Badge tone={badge.tone}>{badge.label}</Badge>}
                    <span className="truncate text-[11px] text-surface-500">{humanize(h.reason)}</span>
                  </div>
                </div>
              </GlowCard>
            );
          })}
        </ScrollSlider>
      )}

      {/* Compare the later picked revision with any other one — or click two cards above. */}
      {afterId && history.length > 1 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] text-surface-500">
            Comparing <span className="font-semibold text-surface-800">{fmtDate(history.find(h => h.id === afterId)!.effectiveFrom)}</span> with
          </span>
          <SelectField
            size="sm"
            className="w-auto"
            value={beforeId ?? ""}
            onChange={e => setSelected(e.target.value ? [e.target.value, afterId] : [afterId])}
          >
            <option value="">Nothing (this revision only)</option>
            {history.filter(h => h.id !== afterId).map(h => (
              <option key={h.id} value={h.id}>
                {fmtDate(h.effectiveFrom)} · ₹{fmtAmt(asNum(h.ctcAnnual))} · {humanize(h.reason)} · {COMP_STATUS_BADGE[h.status]?.label ?? humanize(h.status)}
              </option>
            ))}
          </SelectField>
        </div>
      )}

      {loadErr && <p className="m-0 text-[13px] text-[var(--color-error-fg)]">{loadErr}</p>}
      {after && afterId && (
        <CompCompareTable
          before={beforeId ? before : null}
          after={after}
          beforeLabel={before ? `Earlier (${humanize(before.reason)})` : "Previous"}
          afterLabel={`Later (${humanize(after.reason)})`}
        />
      )}
    </div>
  );
}
